import { useMemo } from 'react';
import { useCollection } from '../lib/hooks';
import type { Campaign, Influencer, Product, Run } from '../../../shared/types';

/**
 * The public statistics tab. Numbers are computed from the runs and campaigns
 * the signed-in user is allowed to see (collection ACLs apply), so a user
 * outside a private avatar's allow-list gets stats without that avatar's
 * campaigns — an admin sees everything. Model prices are NOT readable here:
 * the settings collection is admin-only; this page only sums the cost already
 * recorded on each run.
 */
export default function Statistics() {
  const runs = useCollection<Run>('runs', { sort: '-created' });
  const campaigns = useCollection<Campaign>('campaigns', {
    sort: '-created',
    expand: 'influencer,product',
  });

  // Demo campaigns are presentation material with simulated costs — they
  // must never count as real spend or real output. Members never receive
  // them (ACL); an admin does, so they are dropped here along with their runs.
  const realCampaigns = useMemo(() => campaigns.items.filter((c) => !c.is_demo), [campaigns.items]);
  const realRuns = useMemo(() => {
    const demoIds = new Set(campaigns.items.filter((c) => c.is_demo).map((c) => c.id));
    return runs.items.filter((r) => !demoIds.has(r.campaign));
  }, [runs.items, campaigns.items]);

  const stats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const inMonth = (iso?: string) => Boolean(iso) && new Date(iso as string) >= monthStart;

    // Per-campaign rollup of its runs, so spend can be attributed to the
    // campaign's avatar and product.
    const byCampaign = new Map<string, { cost: number; images: number; videos: number }>();
    const sum = { cost: 0, images: 0, videos: 0, imagesFailed: 0, videosFailed: 0, retries: 0, reviewRounds: 0 };
    const month = { cost: 0, images: 0, videos: 0 };
    for (const run of realRuns) {
      const s = run.stats ?? {};
      const entry = byCampaign.get(run.campaign) ?? { cost: 0, images: 0, videos: 0 };
      entry.cost += s.cost_usd || 0;
      entry.images += s.images_generated || 0;
      entry.videos += s.videos_generated || 0;
      byCampaign.set(run.campaign, entry);
      sum.cost += s.cost_usd || 0;
      sum.images += s.images_generated || 0;
      sum.videos += s.videos_generated || 0;
      sum.imagesFailed += s.images_failed || 0;
      sum.videosFailed += s.videos_failed || 0;
      sum.retries += s.retries || 0;
      sum.reviewRounds += s.review_rounds || 0;
      if (inMonth(run.started || run.created)) {
        month.cost += s.cost_usd || 0;
        month.images += s.images_generated || 0;
        month.videos += s.videos_generated || 0;
      }
    }

    const done = realCampaigns.filter((c) => c.stage === 'done');
    const doneThisMonth = done.filter((c) => inMonth(c.updated));
    const scenesThisMonth = realCampaigns
      .filter((c) => inMonth(c.created))
      .reduce((n, c) => n + (c.scenes?.length || 0), 0);

    // Most used avatar / spend per avatar and product.
    const perAvatar = new Map<string, { name: string; campaigns: number; done: number; cost: number }>();
    const perProduct = new Map<string, { name: string; campaigns: number; cost: number }>();
    for (const c of realCampaigns) {
      const rollup = byCampaign.get(c.id) ?? { cost: 0, images: 0, videos: 0 };
      const inf = c.expand?.influencer as Influencer | undefined;
      const prod = c.expand?.product as Product | undefined;
      const a = perAvatar.get(c.influencer) ?? {
        name: inf?.name || '(deleted avatar)',
        campaigns: 0,
        done: 0,
        cost: 0,
      };
      a.campaigns += 1;
      if (c.stage === 'done') a.done += 1;
      a.cost += rollup.cost;
      perAvatar.set(c.influencer, a);
      const p = perProduct.get(c.product) ?? { name: prod?.name || '(deleted product)', campaigns: 0, cost: 0 };
      p.campaigns += 1;
      p.cost += rollup.cost;
      perProduct.set(c.product, p);
    }
    const avatars = [...perAvatar.values()].sort((a, b) => b.campaigns - a.campaigns);
    const products = [...perProduct.values()].sort((a, b) => b.cost - a.cost);

    // Monthly trend, last 6 months, from run start dates.
    const months: Array<{ label: string; cost: number; images: number; videos: number }> = [];
    for (let i = 5; i >= 0; i--) {
      const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const bucket = { label: start.toLocaleString('en', { month: 'short', year: '2-digit' }), cost: 0, images: 0, videos: 0 };
      for (const run of realRuns) {
        const t = new Date(run.started || run.created || 0);
        if (t >= start && t < end) {
          bucket.cost += run.stats?.cost_usd || 0;
          bucket.images += run.stats?.images_generated || 0;
          bucket.videos += run.stats?.videos_generated || 0;
        }
      }
      months.push(bucket);
    }

    const avgCostPerVideo = done.length
      ? done.reduce((n, c) => n + (byCampaign.get(c.id)?.cost || 0), 0) / done.length
      : 0;

    return { sum, month, done, doneThisMonth, scenesThisMonth, avatars, products, months, avgCostPerVideo };
  }, [realRuns, realCampaigns]);

  if (runs.loading || campaigns.loading) {
    return <div className="zen-card p-6 text-xs text-natural-text/45">Loading statistics…</div>;
  }

  const usd = (n: number) => `$${n.toFixed(2)}`;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total spend (all time)" value={usd(stats.sum.cost)} />
        <StatTile label="Spend this month" value={usd(stats.month.cost)} />
        <StatTile
          label="Videos finished this month"
          value={String(stats.doneThisMonth.length)}
          hint={`${stats.done.length} all time`}
        />
        <StatTile
          label="Avg cost per finished video"
          value={usd(stats.avgCostPerVideo)}
          hint="finished campaigns only"
        />
        <StatTile
          label="Images generated this month"
          value={String(stats.month.images)}
          hint={`${stats.sum.images} all time · ${stats.sum.imagesFailed} failed`}
        />
        <StatTile
          label="Clips generated this month"
          value={String(stats.month.videos)}
          hint={`${stats.sum.videos} all time · ${stats.sum.videosFailed} failed`}
        />
        <StatTile label="Scenes scripted this month" value={String(stats.scenesThisMonth)} />
        <StatTile
          label="Review rounds"
          value={String(stats.sum.reviewRounds)}
          hint={`${stats.sum.retries} provider retries`}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4 items-start">
        <div className="zen-card p-5">
          <h3 className="font-serif text-base font-medium mb-3">Avatars</h3>
          <StatTable
            headers={['Avatar', 'Campaigns', 'Finished', 'Spend']}
            rows={stats.avatars.map((a) => [a.name, String(a.campaigns), String(a.done), usd(a.cost)])}
            empty="No campaigns yet."
          />
        </div>
        <div className="zen-card p-5">
          <h3 className="font-serif text-base font-medium mb-3">Products</h3>
          <StatTable
            headers={['Product', 'Campaigns', 'Spend']}
            rows={stats.products.map((p) => [p.name, String(p.campaigns), usd(p.cost)])}
            empty="No campaigns yet."
          />
        </div>
      </div>

      <div className="zen-card p-5">
        <h3 className="font-serif text-base font-medium mb-3">Last 6 months</h3>
        <StatTable
          headers={['Month', 'Spend', 'Images', 'Clips']}
          rows={stats.months.map((m) => [m.label, usd(m.cost), String(m.images), String(m.videos)])}
          empty="No runs yet."
        />
        <p className="text-[10px] text-natural-text/45 mt-2 leading-relaxed">
          Spend is recorded per generation at the price active at that moment. Campaigns started
          before prices were configured show $0.00 — history is never re-priced.
        </p>
      </div>
    </div>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="zen-card p-4">
      <p className="text-[10px] uppercase tracking-wide text-natural-text/45">{label}</p>
      <p className="font-serif text-xl font-medium mt-1">{value}</p>
      {hint && <p className="text-[10px] text-natural-text/45 mt-0.5">{hint}</p>}
    </div>
  );
}

function StatTable({
  headers,
  rows,
  empty,
}: {
  headers: string[];
  rows: string[][];
  empty: string;
}) {
  if (!rows.length) {
    return <p className="text-xs text-natural-text/45">{empty}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-natural-text/45">
            {headers.map((h) => (
              <th key={h} className="py-1.5 pr-4 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-t border-natural-border/50">
              {row.map((cell, j) => (
                <td key={j} className={`py-1.5 pr-4 ${j > 0 ? 'font-mono' : ''}`}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
